import React, { createContext, useContext, useMemo } from 'react';

export const PagesContentContext = createContext(null);

export const usePagesContent = () => useContext(PagesContentContext);

export const useBaseEntityInfo = () => {
    const context = useContext(PagesContentContext);
    if (context === undefined) {
        throw new Error('useBaseEntityInfo must be used within a PagesContentContext');
    }

    const baseEntity = context?.metaInfo?.globalConfig?.baseEntity || {};
    return {
        baseEntityType : baseEntity?.type || "battery",
        baseEntityTypeIDLable : baseEntity?.idLabel || "Battery ID",
        baseEntityTypeLable : baseEntity?.label || "Battery",
        baseEntityTypePluralLable : baseEntity?.pluralLabel || "Batteries",
        mapIconType : baseEntity?.mapIconType || null,
    }
};

/**
 * Identifier label for the base entity on detail screens, from
 * globalConfig.baseEntity.idLabel. Unlike useBaseEntityInfo this returns the
 * caller's fallback when the org has not configured one, so each screen keeps
 * its own existing copy instead of picking up a shared default.
 */
export const useBaseEntityIdLabel = (fallbackLabel) => {
    const context = useContext(PagesContentContext);
    return context?.metaInfo?.globalConfig?.baseEntity?.idLabel || fallbackLabel;
};

export const useOrgLabels = () => {
    const context = useContext(PagesContentContext);
    const labels = context?.metaInfo?.globalConfig?.labels || {};
    return {
        fleetLabel: labels?.fleet || "Customer Account",
        fleetPluralLabel: labels?.fleetPlural || "Customer Accounts",
        fleetCategoryLabel: labels?.fleetCategory || "Business Segment",
        fleetCategoryPluralLabel: labels?.fleetCategoryPlural || "Business Segments",
        locationLabel: labels?.location || "Operating Location",
        locationPluralLabel: labels?.locationPlural || "Operating Locations",
    };
};

/**
 * Keys the vehicle / battery list APIs behind the alerts, faults and Fleet Performance
 * search boxes filter on: alerts/v2/batteries, diagnostics/v2/batteries and its analytics,
 * and fleet/energy-events/analytics. alerts/v2/analytics takes fewer
 * (SMART_ALERTS_ANALYTICS_SEARCH_KEYS).
 */
export const LIST_SEARCH_KEYS = ["licensePlateNo", "deviceID", "vin", "batteryID"];

/** alerts/v2/analytics filters on these only (alert_analytics_db.py _buildAlertListFilters). */
export const SMART_ALERTS_ANALYTICS_SEARCH_KEYS = ["licensePlateNo", "batteryID"];

/**
 * The org's search options from globalConfig.searchableFields ([{ key, title }]), keeping
 * only entries with a key in allowedKeys, the first of any repeated key, and for an entry
 * without a title the screen's own title for that key (else the key). Returns fallback when
 * the org has not set the list, or nothing in it is usable, so orgs without it keep each
 * screen's own options.
 */
export const resolveSearchableFields = (globalConfig, fallback, allowedKeys = LIST_SEARCH_KEYS) => {
    const configured = globalConfig?.searchableFields;
    if (!Array.isArray(configured)) return fallback;
    const seen = new Set();
    const fields = configured
        .filter((f) => f && typeof f.key === "string" && allowedKeys.includes(f.key) && !seen.has(f.key) && seen.add(f.key))
        .map((f) => ({
            key: f.key,
            title: typeof f.title === "string" && f.title ? f.title : (fallback || []).find((x) => x.key === f.key)?.title || f.key,
        }));
    return fields.length ? fields : fallback;
};

/** resolveSearchableFields for the page in context. Safe outside a PagesContentContext. */
export const useSearchableFields = (fallback, allowedKeys = LIST_SEARCH_KEYS) => {
    const context = useContext(PagesContentContext);
    const configured = context?.metaInfo?.globalConfig?.searchableFields;
    return useMemo(
        () => resolveSearchableFields({ searchableFields: configured }, fallback, allowedKeys),
        [configured, fallback, allowedKeys]
    );
};

/** A React key that changes with the list, so a search box re-reads its default option. */
export const searchFieldsKey = (fields) => (fields || []).map((f) => f.key).join("|");
